/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { WorkAccountingPartner } from '../models/work-accounting-partner';
import { WorkAccountingPartnerContactMatchDto } from '../models/work-accounting-partner-contact-match-dto';
import { WorkAccountingSettings } from '../models/work-accounting-settings';
import { WorkCostCentre } from '../models/work-cost-centre';
import { WorkCreateAccountingPartnerDto } from '../models/work-create-accounting-partner-dto';
import { WorkCreateCostCentreDto } from '../models/work-create-cost-centre-dto';
import { WorkCreateExpensePaymentMethodDto } from '../models/work-create-expense-payment-method-dto';
import { WorkCreateExternalSupplierDto } from '../models/work-create-external-supplier-dto';
import { WorkCreateLedgerAccountDto } from '../models/work-create-ledger-account-dto';
import { WorkCreatePaymentConditionDto } from '../models/work-create-payment-condition-dto';
import { WorkCreateTaxCodeDto } from '../models/work-create-tax-code-dto';
import { WorkExchangeRateDto } from '../models/work-exchange-rate-dto';
import { WorkExpensePaymentMethod } from '../models/work-expense-payment-method';
import { WorkExternalSupplierCreatedDto } from '../models/work-external-supplier-created-dto';
import { WorkLedgerAccount } from '../models/work-ledger-account';
import { WorkLinkAccountingPartnerContactDto } from '../models/work-link-accounting-partner-contact-dto';
import { WorkPaginatedDocumented } from '../models/work-paginated-documented';
import { WorkPaymentCondition } from '../models/work-payment-condition';
import { WorkSavePartnerContainertypeDefaultsDto } from '../models/work-save-partner-containertype-defaults-dto';
import { WorkTaxCode } from '../models/work-tax-code';
import { WorkTaxTypeEnum } from '../models/work-tax-type-enum';
import { WorkUpdateAccountingPartnerDto } from '../models/work-update-accounting-partner-dto';
import { WorkUpdateAccountingSettingsDto } from '../models/work-update-accounting-settings-dto';
import { WorkUpdateCostCentreDto } from '../models/work-update-cost-centre-dto';
import { WorkUpdateExpensePaymentMethodDto } from '../models/work-update-expense-payment-method-dto';
import { WorkUpdateLedgerAccountDto } from '../models/work-update-ledger-account-dto';
import { WorkUpdatePaymentConditionDto } from '../models/work-update-payment-condition-dto';
import { WorkUpdateTaxCodeDto } from '../models/work-update-tax-code-dto';

@Injectable({ providedIn: 'root' })
export class WorkAccountingV2Service extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `accountingV2ControllerFindSettings()` */
  static readonly AccountingV2ControllerFindSettingsPath = '/v2/accounting/settings';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSettings$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkAccountingSettings>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindSettingsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkAccountingSettings>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSettings(
    params?: {
    },
    context?: HttpContext
  ): Observable<WorkAccountingSettings> {
    return this.accountingV2ControllerFindSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkAccountingSettings>): WorkAccountingSettings => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerUpdateSettings()` */
  static readonly AccountingV2ControllerUpdateSettingsPath = '/v2/accounting/settings';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerUpdateSettings()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateSettings$Response(
    params: {
      body: WorkUpdateAccountingSettingsDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkAccountingSettings>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerUpdateSettingsPath, 'patch');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkAccountingSettings>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerUpdateSettings$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateSettings(
    params: {
      body: WorkUpdateAccountingSettingsDto
    },
    context?: HttpContext
  ): Observable<WorkAccountingSettings> {
    return this.accountingV2ControllerUpdateSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkAccountingSettings>): WorkAccountingSettings => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindExportConnectors()` */
  static readonly AccountingV2ControllerFindExportConnectorsPath = '/v2/accounting/export-connectors';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindExportConnectors()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindExportConnectors$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<string>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindExportConnectorsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<string>>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindExportConnectors$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindExportConnectors(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<string>> {
    return this.accountingV2ControllerFindExportConnectors$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<string>>): Array<string> => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerCheckPermissions()` */
  static readonly AccountingV2ControllerCheckPermissionsPath = '/v2/accounting/export-connectors/{connectorKey}/permissions';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerCheckPermissions()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerCheckPermissions$Response(
    params: {
      connectorKey: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<{
}>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerCheckPermissionsPath, 'get');
    if (params) {
      rb.path('connectorKey', params.connectorKey, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        }>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerCheckPermissions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerCheckPermissions(
    params: {
      connectorKey: string;
    },
    context?: HttpContext
  ): Observable<{
}> {
    return this.accountingV2ControllerCheckPermissions$Response(params, context).pipe(
      map((r: StrictHttpResponse<{
}>): {
} => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindLedgerAccounts()` */
  static readonly AccountingV2ControllerFindLedgerAccountsPath = '/v2/accounting/ledger-accounts';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindLedgerAccounts()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindLedgerAccounts$Response(
    params?: {

    /**
     * Page number to retrieve.If you provide invalid value the default page number will applied
     *         <p>
     *              <b>Example: </b> 1
     *           </p>
     *         <p>
     *              <b>Default Value: </b> 1
     *           </p>
     */
      page?: number;

    /**
     * Number of records per page.
     *       <p>
     *              <b>Example: </b> 20
     *           </p>
     *       <p>
     *              <b>Default Value: </b> 100
     *           </p>
     *       <p>
     *              <b>Max Value: </b> 2000
     *           </p>
     *
     *       If provided value is greater than max value, max value will be applied.
     */
      limit?: number;

    /**
     * Filter by active query param.
     *           <p>
     *              <b>Format: </b> filter.active={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.active=$not:$like:John Doe&filter.active=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.active'?: Array<string>;

    /**
     * Filter by selectable query param.
     *           <p>
     *              <b>Format: </b> filter.selectable={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.selectable=$not:$like:John Doe&filter.selectable=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.selectable'?: Array<string>;

    /**
     * Filter by approvalRequired query param.
     *           <p>
     *              <b>Format: </b> filter.approvalRequired={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.approvalRequired=$not:$like:John Doe&filter.approvalRequired=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.approvalRequired'?: Array<string>;

    /**
     * Filter by accountType query param.
     *           <p>
     *              <b>Format: </b> filter.accountType={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.accountType=$not:$like:John Doe&filter.accountType=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$in</li></ul>
     */
      'filter.accountType'?: Array<string>;

    /**
     * Parameter to sort by.
     *       <p>To sort by multiple fields, just provide query param multiple types. The order in url defines an order of sorting</p>
     *       <p>
     *              <b>Format: </b> fieldName:DIRECTION
     *           </p>
     *       <p>
     *              <b>Example: </b> sortBy=id:DESC&sortBy=createdAt:ASC
     *           </p>
     *       <p>
     *              <b>Default Value: </b> number:ASC
     *           </p>
     *       <h4>Available Fields</h4><ul><li>id</li>
     * <li>number</li>
     * <li>name</li>
     * <li>active</li>
     * <li>selectable</li></ul>
     */
      sortBy?: Array<'id:ASC' | 'id:DESC' | 'number:ASC' | 'number:DESC' | 'name:ASC' | 'name:DESC' | 'active:ASC' | 'active:DESC' | 'selectable:ASC' | 'selectable:DESC'>;

    /**
     * Search term to filter result values
     *         <p>
     *              <b>Example: </b> John
     *           </p>
     *         <p>
     *              <b>Default Value: </b> No default value
     *           </p>
     */
      search?: string;

    /**
     * List of fields to search by term to filter result values
     *         <p>
     *              <b>Example: </b> number,name
     *           </p>
     *         <p>
     *              <b>Default Value: </b> By default all fields mentioned below will be used to search by term
     *           </p>
     *         <h4>Available Fields</h4><ul><li>number</li>
     * <li>name</li></ul>
     */
      searchBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkPaginatedDocumented & {
'data'?: Array<WorkLedgerAccount>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'active'?: (string | Array<string>);
'selectable'?: (string | Array<string>);
'approvalRequired'?: (string | Array<string>);
'accountType'?: (string | Array<string>);
};
};
}>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindLedgerAccountsPath, 'get');
    if (params) {
      rb.query('page', params.page, {});
      rb.query('limit', params.limit, {});
      rb.query('filter.active', params['filter.active'], {});
      rb.query('filter.selectable', params['filter.selectable'], {});
      rb.query('filter.approvalRequired', params['filter.approvalRequired'], {});
      rb.query('filter.accountType', params['filter.accountType'], {});
      rb.query('sortBy', params.sortBy, {});
      rb.query('search', params.search, {});
      rb.query('searchBy', params.searchBy, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkPaginatedDocumented & {
        'data'?: Array<WorkLedgerAccount>;
        'meta'?: {
        'select'?: Array<string>;
        'filter'?: {
        'active'?: (string | Array<string>);
        'selectable'?: (string | Array<string>);
        'approvalRequired'?: (string | Array<string>);
        'accountType'?: (string | Array<string>);
        };
        };
        }>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindLedgerAccounts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindLedgerAccounts(
    params?: {

    /**
     * Page number to retrieve.If you provide invalid value the default page number will applied
     *         <p>
     *              <b>Example: </b> 1
     *           </p>
     *         <p>
     *              <b>Default Value: </b> 1
     *           </p>
     */
      page?: number;

    /**
     * Number of records per page.
     *       <p>
     *              <b>Example: </b> 20
     *           </p>
     *       <p>
     *              <b>Default Value: </b> 100
     *           </p>
     *       <p>
     *              <b>Max Value: </b> 2000
     *           </p>
     *
     *       If provided value is greater than max value, max value will be applied.
     */
      limit?: number;

    /**
     * Filter by active query param.
     *           <p>
     *              <b>Format: </b> filter.active={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.active=$not:$like:John Doe&filter.active=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.active'?: Array<string>;

    /**
     * Filter by selectable query param.
     *           <p>
     *              <b>Format: </b> filter.selectable={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.selectable=$not:$like:John Doe&filter.selectable=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.selectable'?: Array<string>;

    /**
     * Filter by approvalRequired query param.
     *           <p>
     *              <b>Format: </b> filter.approvalRequired={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.approvalRequired=$not:$like:John Doe&filter.approvalRequired=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.approvalRequired'?: Array<string>;

    /**
     * Filter by accountType query param.
     *           <p>
     *              <b>Format: </b> filter.accountType={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.accountType=$not:$like:John Doe&filter.accountType=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$in</li></ul>
     */
      'filter.accountType'?: Array<string>;

    /**
     * Parameter to sort by.
     *       <p>To sort by multiple fields, just provide query param multiple types. The order in url defines an order of sorting</p>
     *       <p>
     *              <b>Format: </b> fieldName:DIRECTION
     *           </p>
     *       <p>
     *              <b>Example: </b> sortBy=id:DESC&sortBy=createdAt:ASC
     *           </p>
     *       <p>
     *              <b>Default Value: </b> number:ASC
     *           </p>
     *       <h4>Available Fields</h4><ul><li>id</li>
     * <li>number</li>
     * <li>name</li>
     * <li>active</li>
     * <li>selectable</li></ul>
     */
      sortBy?: Array<'id:ASC' | 'id:DESC' | 'number:ASC' | 'number:DESC' | 'name:ASC' | 'name:DESC' | 'active:ASC' | 'active:DESC' | 'selectable:ASC' | 'selectable:DESC'>;

    /**
     * Search term to filter result values
     *         <p>
     *              <b>Example: </b> John
     *           </p>
     *         <p>
     *              <b>Default Value: </b> No default value
     *           </p>
     */
      search?: string;

    /**
     * List of fields to search by term to filter result values
     *         <p>
     *              <b>Example: </b> number,name
     *           </p>
     *         <p>
     *              <b>Default Value: </b> By default all fields mentioned below will be used to search by term
     *           </p>
     *         <h4>Available Fields</h4><ul><li>number</li>
     * <li>name</li></ul>
     */
      searchBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<WorkPaginatedDocumented & {
'data'?: Array<WorkLedgerAccount>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'active'?: (string | Array<string>);
'selectable'?: (string | Array<string>);
'approvalRequired'?: (string | Array<string>);
'accountType'?: (string | Array<string>);
};
};
}> {
    return this.accountingV2ControllerFindLedgerAccounts$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkPaginatedDocumented & {
'data'?: Array<WorkLedgerAccount>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'active'?: (string | Array<string>);
'selectable'?: (string | Array<string>);
'approvalRequired'?: (string | Array<string>);
'accountType'?: (string | Array<string>);
};
};
}>): WorkPaginatedDocumented & {
'data'?: Array<WorkLedgerAccount>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'active'?: (string | Array<string>);
'selectable'?: (string | Array<string>);
'approvalRequired'?: (string | Array<string>);
'accountType'?: (string | Array<string>);
};
};
} => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerCreateLedgerAccount()` */
  static readonly AccountingV2ControllerCreateLedgerAccountPath = '/v2/accounting/ledger-accounts';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerCreateLedgerAccount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateLedgerAccount$Response(
    params: {
      body: WorkCreateLedgerAccountDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkLedgerAccount>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerCreateLedgerAccountPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkLedgerAccount>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerCreateLedgerAccount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateLedgerAccount(
    params: {
      body: WorkCreateLedgerAccountDto
    },
    context?: HttpContext
  ): Observable<WorkLedgerAccount> {
    return this.accountingV2ControllerCreateLedgerAccount$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkLedgerAccount>): WorkLedgerAccount => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindSelectableLedgerAccounts()` */
  static readonly AccountingV2ControllerFindSelectableLedgerAccountsPath = '/v2/accounting/ledger-accounts/selectable';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindSelectableLedgerAccounts()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSelectableLedgerAccounts$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkLedgerAccount>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindSelectableLedgerAccountsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkLedgerAccount>>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindSelectableLedgerAccounts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSelectableLedgerAccounts(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<WorkLedgerAccount>> {
    return this.accountingV2ControllerFindSelectableLedgerAccounts$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkLedgerAccount>>): Array<WorkLedgerAccount> => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerUpdateLedgerAccount()` */
  static readonly AccountingV2ControllerUpdateLedgerAccountPath = '/v2/accounting/ledger-accounts/{id}';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerUpdateLedgerAccount()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateLedgerAccount$Response(
    params: {
      id: number;
      body: WorkUpdateLedgerAccountDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkLedgerAccount>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerUpdateLedgerAccountPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkLedgerAccount>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerUpdateLedgerAccount$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateLedgerAccount(
    params: {
      id: number;
      body: WorkUpdateLedgerAccountDto
    },
    context?: HttpContext
  ): Observable<WorkLedgerAccount> {
    return this.accountingV2ControllerUpdateLedgerAccount$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkLedgerAccount>): WorkLedgerAccount => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindCostCentres()` */
  static readonly AccountingV2ControllerFindCostCentresPath = '/v2/accounting/cost-centres';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindCostCentres()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindCostCentres$Response(
    params?: {

    /**
     * Page number to retrieve.If you provide invalid value the default page number will applied
     *         <p>
     *              <b>Example: </b> 1
     *           </p>
     *         <p>
     *              <b>Default Value: </b> 1
     *           </p>
     */
      page?: number;

    /**
     * Number of records per page.
     *       <p>
     *              <b>Example: </b> 20
     *           </p>
     *       <p>
     *              <b>Default Value: </b> 100
     *           </p>
     *       <p>
     *              <b>Max Value: </b> 2000
     *           </p>
     *
     *       If provided value is greater than max value, max value will be applied.
     */
      limit?: number;

    /**
     * Filter by active query param.
     *           <p>
     *              <b>Format: </b> filter.active={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.active=$not:$like:John Doe&filter.active=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.active'?: Array<string>;

    /**
     * Filter by selectable query param.
     *           <p>
     *              <b>Format: </b> filter.selectable={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.selectable=$not:$like:John Doe&filter.selectable=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.selectable'?: Array<string>;

    /**
     * Parameter to sort by.
     *       <p>To sort by multiple fields, just provide query param multiple types. The order in url defines an order of sorting</p>
     *       <p>
     *              <b>Format: </b> fieldName:DIRECTION
     *           </p>
     *       <p>
     *              <b>Example: </b> sortBy=id:DESC&sortBy=createdAt:ASC
     *           </p>
     *       <p>
     *              <b>Default Value: </b> number:ASC
     *           </p>
     *       <h4>Available Fields</h4><ul><li>id</li>
     * <li>number</li>
     * <li>name</li>
     * <li>active</li>
     * <li>selectable</li></ul>
     */
      sortBy?: Array<'id:ASC' | 'id:DESC' | 'number:ASC' | 'number:DESC' | 'name:ASC' | 'name:DESC' | 'active:ASC' | 'active:DESC' | 'selectable:ASC' | 'selectable:DESC'>;

    /**
     * Search term to filter result values
     *         <p>
     *              <b>Example: </b> John
     *           </p>
     *         <p>
     *              <b>Default Value: </b> No default value
     *           </p>
     */
      search?: string;

    /**
     * List of fields to search by term to filter result values
     *         <p>
     *              <b>Example: </b> number,name
     *           </p>
     *         <p>
     *              <b>Default Value: </b> By default all fields mentioned below will be used to search by term
     *           </p>
     *         <h4>Available Fields</h4><ul><li>number</li>
     * <li>name</li></ul>
     */
      searchBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkPaginatedDocumented & {
'data'?: Array<WorkCostCentre>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'active'?: (string | Array<string>);
'selectable'?: (string | Array<string>);
};
};
}>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindCostCentresPath, 'get');
    if (params) {
      rb.query('page', params.page, {});
      rb.query('limit', params.limit, {});
      rb.query('filter.active', params['filter.active'], {});
      rb.query('filter.selectable', params['filter.selectable'], {});
      rb.query('sortBy', params.sortBy, {});
      rb.query('search', params.search, {});
      rb.query('searchBy', params.searchBy, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkPaginatedDocumented & {
        'data'?: Array<WorkCostCentre>;
        'meta'?: {
        'select'?: Array<string>;
        'filter'?: {
        'active'?: (string | Array<string>);
        'selectable'?: (string | Array<string>);
        };
        };
        }>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindCostCentres$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindCostCentres(
    params?: {

    /**
     * Page number to retrieve.If you provide invalid value the default page number will applied
     *         <p>
     *              <b>Example: </b> 1
     *           </p>
     *         <p>
     *              <b>Default Value: </b> 1
     *           </p>
     */
      page?: number;

    /**
     * Number of records per page.
     *       <p>
     *              <b>Example: </b> 20
     *           </p>
     *       <p>
     *              <b>Default Value: </b> 100
     *           </p>
     *       <p>
     *              <b>Max Value: </b> 2000
     *           </p>
     *
     *       If provided value is greater than max value, max value will be applied.
     */
      limit?: number;

    /**
     * Filter by active query param.
     *           <p>
     *              <b>Format: </b> filter.active={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.active=$not:$like:John Doe&filter.active=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.active'?: Array<string>;

    /**
     * Filter by selectable query param.
     *           <p>
     *              <b>Format: </b> filter.selectable={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.selectable=$not:$like:John Doe&filter.selectable=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.selectable'?: Array<string>;

    /**
     * Parameter to sort by.
     *       <p>To sort by multiple fields, just provide query param multiple types. The order in url defines an order of sorting</p>
     *       <p>
     *              <b>Format: </b> fieldName:DIRECTION
     *           </p>
     *       <p>
     *              <b>Example: </b> sortBy=id:DESC&sortBy=createdAt:ASC
     *           </p>
     *       <p>
     *              <b>Default Value: </b> number:ASC
     *           </p>
     *       <h4>Available Fields</h4><ul><li>id</li>
     * <li>number</li>
     * <li>name</li>
     * <li>active</li>
     * <li>selectable</li></ul>
     */
      sortBy?: Array<'id:ASC' | 'id:DESC' | 'number:ASC' | 'number:DESC' | 'name:ASC' | 'name:DESC' | 'active:ASC' | 'active:DESC' | 'selectable:ASC' | 'selectable:DESC'>;

    /**
     * Search term to filter result values
     *         <p>
     *              <b>Example: </b> John
     *           </p>
     *         <p>
     *              <b>Default Value: </b> No default value
     *           </p>
     */
      search?: string;

    /**
     * List of fields to search by term to filter result values
     *         <p>
     *              <b>Example: </b> number,name
     *           </p>
     *         <p>
     *              <b>Default Value: </b> By default all fields mentioned below will be used to search by term
     *           </p>
     *         <h4>Available Fields</h4><ul><li>number</li>
     * <li>name</li></ul>
     */
      searchBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<WorkPaginatedDocumented & {
'data'?: Array<WorkCostCentre>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'active'?: (string | Array<string>);
'selectable'?: (string | Array<string>);
};
};
}> {
    return this.accountingV2ControllerFindCostCentres$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkPaginatedDocumented & {
'data'?: Array<WorkCostCentre>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'active'?: (string | Array<string>);
'selectable'?: (string | Array<string>);
};
};
}>): WorkPaginatedDocumented & {
'data'?: Array<WorkCostCentre>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'active'?: (string | Array<string>);
'selectable'?: (string | Array<string>);
};
};
} => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerCreateCostCentre()` */
  static readonly AccountingV2ControllerCreateCostCentrePath = '/v2/accounting/cost-centres';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerCreateCostCentre()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateCostCentre$Response(
    params: {
      body: WorkCreateCostCentreDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkCostCentre>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerCreateCostCentrePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkCostCentre>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerCreateCostCentre$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateCostCentre(
    params: {
      body: WorkCreateCostCentreDto
    },
    context?: HttpContext
  ): Observable<WorkCostCentre> {
    return this.accountingV2ControllerCreateCostCentre$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkCostCentre>): WorkCostCentre => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindSelectableCostCentres()` */
  static readonly AccountingV2ControllerFindSelectableCostCentresPath = '/v2/accounting/cost-centres/selectable';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindSelectableCostCentres()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSelectableCostCentres$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkCostCentre>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindSelectableCostCentresPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkCostCentre>>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindSelectableCostCentres$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSelectableCostCentres(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<WorkCostCentre>> {
    return this.accountingV2ControllerFindSelectableCostCentres$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkCostCentre>>): Array<WorkCostCentre> => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerUpdateCostCentre()` */
  static readonly AccountingV2ControllerUpdateCostCentrePath = '/v2/accounting/cost-centres/{id}';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerUpdateCostCentre()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateCostCentre$Response(
    params: {
      id: number;
      body: WorkUpdateCostCentreDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkCostCentre>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerUpdateCostCentrePath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkCostCentre>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerUpdateCostCentre$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateCostCentre(
    params: {
      id: number;
      body: WorkUpdateCostCentreDto
    },
    context?: HttpContext
  ): Observable<WorkCostCentre> {
    return this.accountingV2ControllerUpdateCostCentre$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkCostCentre>): WorkCostCentre => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindTaxCodes()` */
  static readonly AccountingV2ControllerFindTaxCodesPath = '/v2/accounting/tax-codes';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindTaxCodes()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindTaxCodes$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkTaxCode>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindTaxCodesPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkTaxCode>>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindTaxCodes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindTaxCodes(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<WorkTaxCode>> {
    return this.accountingV2ControllerFindTaxCodes$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkTaxCode>>): Array<WorkTaxCode> => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerCreateTaxCode()` */
  static readonly AccountingV2ControllerCreateTaxCodePath = '/v2/accounting/tax-codes';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerCreateTaxCode()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateTaxCode$Response(
    params: {
      body: WorkCreateTaxCodeDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkTaxCode>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerCreateTaxCodePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkTaxCode>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerCreateTaxCode$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateTaxCode(
    params: {
      body: WorkCreateTaxCodeDto
    },
    context?: HttpContext
  ): Observable<WorkTaxCode> {
    return this.accountingV2ControllerCreateTaxCode$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkTaxCode>): WorkTaxCode => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindSelectableTaxCodes()` */
  static readonly AccountingV2ControllerFindSelectableTaxCodesPath = '/v2/accounting/tax-codes/selectable';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindSelectableTaxCodes()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSelectableTaxCodes$Response(
    params: {
      taxType: WorkTaxTypeEnum;

    /**
     * Stichtag (Belegdatum), YYYY-MM-DD
     */
      date: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkTaxCode>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindSelectableTaxCodesPath, 'get');
    if (params) {
      rb.query('taxType', params.taxType, {});
      rb.query('date', params.date, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkTaxCode>>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindSelectableTaxCodes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSelectableTaxCodes(
    params: {
      taxType: WorkTaxTypeEnum;

    /**
     * Stichtag (Belegdatum), YYYY-MM-DD
     */
      date: string;
    },
    context?: HttpContext
  ): Observable<Array<WorkTaxCode>> {
    return this.accountingV2ControllerFindSelectableTaxCodes$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkTaxCode>>): Array<WorkTaxCode> => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerUpdateTaxCode()` */
  static readonly AccountingV2ControllerUpdateTaxCodePath = '/v2/accounting/tax-codes/{id}';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerUpdateTaxCode()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateTaxCode$Response(
    params: {
      id: number;
      body: WorkUpdateTaxCodeDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkTaxCode>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerUpdateTaxCodePath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkTaxCode>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerUpdateTaxCode$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateTaxCode(
    params: {
      id: number;
      body: WorkUpdateTaxCodeDto
    },
    context?: HttpContext
  ): Observable<WorkTaxCode> {
    return this.accountingV2ControllerUpdateTaxCode$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkTaxCode>): WorkTaxCode => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindPartners()` */
  static readonly AccountingV2ControllerFindPartnersPath = '/v2/accounting/partners';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindPartners()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindPartners$Response(
    params?: {

    /**
     * Page number to retrieve.If you provide invalid value the default page number will applied
     *         <p>
     *              <b>Example: </b> 1
     *           </p>
     *         <p>
     *              <b>Default Value: </b> 1
     *           </p>
     */
      page?: number;

    /**
     * Number of records per page.
     *       <p>
     *              <b>Example: </b> 20
     *           </p>
     *       <p>
     *              <b>Default Value: </b> 100
     *           </p>
     *       <p>
     *              <b>Max Value: </b> 5000
     *           </p>
     *
     *       If provided value is greater than max value, max value will be applied.
     */
      limit?: number;

    /**
     * Filter by partnerType query param.
     *           <p>
     *              <b>Format: </b> filter.partnerType={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.partnerType=$not:$like:John Doe&filter.partnerType=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.partnerType'?: Array<string>;

    /**
     * Filter by active query param.
     *           <p>
     *              <b>Format: </b> filter.active={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.active=$not:$like:John Doe&filter.active=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.active'?: Array<string>;

    /**
     * Filter by contactId query param.
     *           <p>
     *              <b>Format: </b> filter.contactId={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.contactId=$not:$like:John Doe&filter.contactId=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$null</li></ul>
     */
      'filter.contactId'?: Array<string>;

    /**
     * Filter by userId query param.
     *           <p>
     *              <b>Format: </b> filter.userId={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.userId=$not:$like:John Doe&filter.userId=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$null</li></ul>
     */
      'filter.userId'?: Array<string>;

    /**
     * Parameter to sort by.
     *       <p>To sort by multiple fields, just provide query param multiple types. The order in url defines an order of sorting</p>
     *       <p>
     *              <b>Format: </b> fieldName:DIRECTION
     *           </p>
     *       <p>
     *              <b>Example: </b> sortBy=id:DESC&sortBy=createdAt:ASC
     *           </p>
     *       <p>
     *              <b>Default Value: </b> name:ASC
     *           </p>
     *       <h4>Available Fields</h4><ul><li>id</li>
     * <li>number</li>
     * <li>name</li>
     * <li>shortName</li>
     * <li>active</li></ul>
     */
      sortBy?: Array<'id:ASC' | 'id:DESC' | 'number:ASC' | 'number:DESC' | 'name:ASC' | 'name:DESC' | 'shortName:ASC' | 'shortName:DESC' | 'active:ASC' | 'active:DESC'>;

    /**
     * Search term to filter result values
     *         <p>
     *              <b>Example: </b> John
     *           </p>
     *         <p>
     *              <b>Default Value: </b> No default value
     *           </p>
     */
      search?: string;

    /**
     * List of fields to search by term to filter result values
     *         <p>
     *              <b>Example: </b> number,name,shortName,iban,contact.company
     *           </p>
     *         <p>
     *              <b>Default Value: </b> By default all fields mentioned below will be used to search by term
     *           </p>
     *         <h4>Available Fields</h4><ul><li>number</li>
     * <li>name</li>
     * <li>shortName</li>
     * <li>iban</li>
     * <li>contact.company</li></ul>
     */
      searchBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkPaginatedDocumented & {
'data'?: Array<WorkAccountingPartner>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'partnerType'?: (string | Array<string>);
'active'?: (string | Array<string>);
'contactId'?: (string | Array<string>);
'userId'?: (string | Array<string>);
};
};
}>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindPartnersPath, 'get');
    if (params) {
      rb.query('page', params.page, {});
      rb.query('limit', params.limit, {});
      rb.query('filter.partnerType', params['filter.partnerType'], {});
      rb.query('filter.active', params['filter.active'], {});
      rb.query('filter.contactId', params['filter.contactId'], {});
      rb.query('filter.userId', params['filter.userId'], {});
      rb.query('sortBy', params.sortBy, {});
      rb.query('search', params.search, {});
      rb.query('searchBy', params.searchBy, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkPaginatedDocumented & {
        'data'?: Array<WorkAccountingPartner>;
        'meta'?: {
        'select'?: Array<string>;
        'filter'?: {
        'partnerType'?: (string | Array<string>);
        'active'?: (string | Array<string>);
        'contactId'?: (string | Array<string>);
        'userId'?: (string | Array<string>);
        };
        };
        }>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindPartners$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindPartners(
    params?: {

    /**
     * Page number to retrieve.If you provide invalid value the default page number will applied
     *         <p>
     *              <b>Example: </b> 1
     *           </p>
     *         <p>
     *              <b>Default Value: </b> 1
     *           </p>
     */
      page?: number;

    /**
     * Number of records per page.
     *       <p>
     *              <b>Example: </b> 20
     *           </p>
     *       <p>
     *              <b>Default Value: </b> 100
     *           </p>
     *       <p>
     *              <b>Max Value: </b> 5000
     *           </p>
     *
     *       If provided value is greater than max value, max value will be applied.
     */
      limit?: number;

    /**
     * Filter by partnerType query param.
     *           <p>
     *              <b>Format: </b> filter.partnerType={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.partnerType=$not:$like:John Doe&filter.partnerType=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.partnerType'?: Array<string>;

    /**
     * Filter by active query param.
     *           <p>
     *              <b>Format: </b> filter.active={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.active=$not:$like:John Doe&filter.active=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li></ul>
     */
      'filter.active'?: Array<string>;

    /**
     * Filter by contactId query param.
     *           <p>
     *              <b>Format: </b> filter.contactId={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.contactId=$not:$like:John Doe&filter.contactId=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$null</li></ul>
     */
      'filter.contactId'?: Array<string>;

    /**
     * Filter by userId query param.
     *           <p>
     *              <b>Format: </b> filter.userId={$not}:OPERATION:VALUE
     *           </p>
     *           <p>
     *              <b>Example: </b> filter.userId=$not:$like:John Doe&filter.userId=like:John
     *           </p>
     *           <h4>Available Operations</h4><ul><li>$eq</li>
     * <li>$null</li></ul>
     */
      'filter.userId'?: Array<string>;

    /**
     * Parameter to sort by.
     *       <p>To sort by multiple fields, just provide query param multiple types. The order in url defines an order of sorting</p>
     *       <p>
     *              <b>Format: </b> fieldName:DIRECTION
     *           </p>
     *       <p>
     *              <b>Example: </b> sortBy=id:DESC&sortBy=createdAt:ASC
     *           </p>
     *       <p>
     *              <b>Default Value: </b> name:ASC
     *           </p>
     *       <h4>Available Fields</h4><ul><li>id</li>
     * <li>number</li>
     * <li>name</li>
     * <li>shortName</li>
     * <li>active</li></ul>
     */
      sortBy?: Array<'id:ASC' | 'id:DESC' | 'number:ASC' | 'number:DESC' | 'name:ASC' | 'name:DESC' | 'shortName:ASC' | 'shortName:DESC' | 'active:ASC' | 'active:DESC'>;

    /**
     * Search term to filter result values
     *         <p>
     *              <b>Example: </b> John
     *           </p>
     *         <p>
     *              <b>Default Value: </b> No default value
     *           </p>
     */
      search?: string;

    /**
     * List of fields to search by term to filter result values
     *         <p>
     *              <b>Example: </b> number,name,shortName,iban,contact.company
     *           </p>
     *         <p>
     *              <b>Default Value: </b> By default all fields mentioned below will be used to search by term
     *           </p>
     *         <h4>Available Fields</h4><ul><li>number</li>
     * <li>name</li>
     * <li>shortName</li>
     * <li>iban</li>
     * <li>contact.company</li></ul>
     */
      searchBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<WorkPaginatedDocumented & {
'data'?: Array<WorkAccountingPartner>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'partnerType'?: (string | Array<string>);
'active'?: (string | Array<string>);
'contactId'?: (string | Array<string>);
'userId'?: (string | Array<string>);
};
};
}> {
    return this.accountingV2ControllerFindPartners$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkPaginatedDocumented & {
'data'?: Array<WorkAccountingPartner>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'partnerType'?: (string | Array<string>);
'active'?: (string | Array<string>);
'contactId'?: (string | Array<string>);
'userId'?: (string | Array<string>);
};
};
}>): WorkPaginatedDocumented & {
'data'?: Array<WorkAccountingPartner>;
'meta'?: {
'select'?: Array<string>;
'filter'?: {
'partnerType'?: (string | Array<string>);
'active'?: (string | Array<string>);
'contactId'?: (string | Array<string>);
'userId'?: (string | Array<string>);
};
};
} => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerCreatePartner()` */
  static readonly AccountingV2ControllerCreatePartnerPath = '/v2/accounting/partners';

  /**
   * Required permissions: accounting:admin, vouchers:create, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerCreatePartner()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreatePartner$Response(
    params: {
      body: WorkCreateAccountingPartnerDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkAccountingPartner>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerCreatePartnerPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkAccountingPartner>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin, vouchers:create, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerCreatePartner$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreatePartner(
    params: {
      body: WorkCreateAccountingPartnerDto
    },
    context?: HttpContext
  ): Observable<WorkAccountingPartner> {
    return this.accountingV2ControllerCreatePartner$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkAccountingPartner>): WorkAccountingPartner => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindActivePartners()` */
  static readonly AccountingV2ControllerFindActivePartnersPath = '/v2/accounting/partners/active/{partnerType}';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindActivePartners()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindActivePartners$Response(
    params: {
      partnerType: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkAccountingPartner>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindActivePartnersPath, 'get');
    if (params) {
      rb.path('partnerType', params.partnerType, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkAccountingPartner>>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindActivePartners$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindActivePartners(
    params: {
      partnerType: string;
    },
    context?: HttpContext
  ): Observable<Array<WorkAccountingPartner>> {
    return this.accountingV2ControllerFindActivePartners$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkAccountingPartner>>): Array<WorkAccountingPartner> => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindPartner()` */
  static readonly AccountingV2ControllerFindPartnerPath = '/v2/accounting/partners/{id}';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindPartner()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindPartner$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkAccountingPartner>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindPartnerPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkAccountingPartner>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindPartner$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindPartner(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkAccountingPartner> {
    return this.accountingV2ControllerFindPartner$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkAccountingPartner>): WorkAccountingPartner => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerUpdatePartner()` */
  static readonly AccountingV2ControllerUpdatePartnerPath = '/v2/accounting/partners/{id}';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerUpdatePartner()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdatePartner$Response(
    params: {
      id: number;
      body: WorkUpdateAccountingPartnerDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkAccountingPartner>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerUpdatePartnerPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkAccountingPartner>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerUpdatePartner$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdatePartner(
    params: {
      id: number;
      body: WorkUpdateAccountingPartnerDto
    },
    context?: HttpContext
  ): Observable<WorkAccountingPartner> {
    return this.accountingV2ControllerUpdatePartner$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkAccountingPartner>): WorkAccountingPartner => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerMatchPartnerContact()` */
  static readonly AccountingV2ControllerMatchPartnerContactPath = '/v2/accounting/partners/{id}/contact-match';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerMatchPartnerContact()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerMatchPartnerContact$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkAccountingPartnerContactMatchDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerMatchPartnerContactPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkAccountingPartnerContactMatchDto>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerMatchPartnerContact$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerMatchPartnerContact(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkAccountingPartnerContactMatchDto> {
    return this.accountingV2ControllerMatchPartnerContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkAccountingPartnerContactMatchDto>): WorkAccountingPartnerContactMatchDto => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerCreateExternalSupplier()` */
  static readonly AccountingV2ControllerCreateExternalSupplierPath = '/v2/accounting/partners/external-supplier';

  /**
   * Required permissions: accounting:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerCreateExternalSupplier()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateExternalSupplier$Response(
    params: {
      body: WorkCreateExternalSupplierDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkExternalSupplierCreatedDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerCreateExternalSupplierPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkExternalSupplierCreatedDto>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerCreateExternalSupplier$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateExternalSupplier(
    params: {
      body: WorkCreateExternalSupplierDto
    },
    context?: HttpContext
  ): Observable<WorkExternalSupplierCreatedDto> {
    return this.accountingV2ControllerCreateExternalSupplier$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkExternalSupplierCreatedDto>): WorkExternalSupplierCreatedDto => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerAutoAssignContacts()` */
  static readonly AccountingV2ControllerAutoAssignContactsPath = '/v2/accounting/partners/auto-assign-contacts';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerAutoAssignContacts()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerAutoAssignContacts$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<number>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerAutoAssignContactsPath, 'post');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: parseFloat(String((r as HttpResponse<any>).body)) }) as StrictHttpResponse<number>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerAutoAssignContacts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerAutoAssignContacts(
    params?: {
    },
    context?: HttpContext
  ): Observable<number> {
    return this.accountingV2ControllerAutoAssignContacts$Response(params, context).pipe(
      map((r: StrictHttpResponse<number>): number => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerLinkPartnerContact()` */
  static readonly AccountingV2ControllerLinkPartnerContactPath = '/v2/accounting/partners/{id}/contact';

  /**
   * Required permissions: accounting:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerLinkPartnerContact()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerLinkPartnerContact$Response(
    params: {
      id: number;
      body: WorkLinkAccountingPartnerContactDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkAccountingPartner>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerLinkPartnerContactPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkAccountingPartner>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerLinkPartnerContact$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerLinkPartnerContact(
    params: {
      id: number;
      body: WorkLinkAccountingPartnerContactDto
    },
    context?: HttpContext
  ): Observable<WorkAccountingPartner> {
    return this.accountingV2ControllerLinkPartnerContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkAccountingPartner>): WorkAccountingPartner => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerSavePartnerContainertypeDefaults()` */
  static readonly AccountingV2ControllerSavePartnerContainertypeDefaultsPath = '/v2/accounting/partners/{id}/containertype-defaults';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerSavePartnerContainertypeDefaults()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerSavePartnerContainertypeDefaults$Response(
    params: {
      id: number;
      body: WorkSavePartnerContainertypeDefaultsDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkAccountingPartner>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerSavePartnerContainertypeDefaultsPath, 'put');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkAccountingPartner>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerSavePartnerContainertypeDefaults$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerSavePartnerContainertypeDefaults(
    params: {
      id: number;
      body: WorkSavePartnerContainertypeDefaultsDto
    },
    context?: HttpContext
  ): Observable<WorkAccountingPartner> {
    return this.accountingV2ControllerSavePartnerContainertypeDefaults$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkAccountingPartner>): WorkAccountingPartner => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindPaymentConditions()` */
  static readonly AccountingV2ControllerFindPaymentConditionsPath = '/v2/accounting/payment-conditions';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindPaymentConditions()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindPaymentConditions$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkPaymentCondition>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindPaymentConditionsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkPaymentCondition>>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindPaymentConditions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindPaymentConditions(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<WorkPaymentCondition>> {
    return this.accountingV2ControllerFindPaymentConditions$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkPaymentCondition>>): Array<WorkPaymentCondition> => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerCreatePaymentCondition()` */
  static readonly AccountingV2ControllerCreatePaymentConditionPath = '/v2/accounting/payment-conditions';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerCreatePaymentCondition()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreatePaymentCondition$Response(
    params: {
      body: WorkCreatePaymentConditionDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkPaymentCondition>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerCreatePaymentConditionPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkPaymentCondition>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerCreatePaymentCondition$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreatePaymentCondition(
    params: {
      body: WorkCreatePaymentConditionDto
    },
    context?: HttpContext
  ): Observable<WorkPaymentCondition> {
    return this.accountingV2ControllerCreatePaymentCondition$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkPaymentCondition>): WorkPaymentCondition => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindSelectablePaymentConditions()` */
  static readonly AccountingV2ControllerFindSelectablePaymentConditionsPath = '/v2/accounting/payment-conditions/selectable';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindSelectablePaymentConditions()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSelectablePaymentConditions$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkPaymentCondition>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindSelectablePaymentConditionsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkPaymentCondition>>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindSelectablePaymentConditions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindSelectablePaymentConditions(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<WorkPaymentCondition>> {
    return this.accountingV2ControllerFindSelectablePaymentConditions$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkPaymentCondition>>): Array<WorkPaymentCondition> => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerUpdatePaymentCondition()` */
  static readonly AccountingV2ControllerUpdatePaymentConditionPath = '/v2/accounting/payment-conditions/{id}';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerUpdatePaymentCondition()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdatePaymentCondition$Response(
    params: {
      id: number;
      body: WorkUpdatePaymentConditionDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkPaymentCondition>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerUpdatePaymentConditionPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkPaymentCondition>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerUpdatePaymentCondition$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdatePaymentCondition(
    params: {
      id: number;
      body: WorkUpdatePaymentConditionDto
    },
    context?: HttpContext
  ): Observable<WorkPaymentCondition> {
    return this.accountingV2ControllerUpdatePaymentCondition$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkPaymentCondition>): WorkPaymentCondition => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerFindExpensePaymentMethods()` */
  static readonly AccountingV2ControllerFindExpensePaymentMethodsPath = '/v2/accounting/expense-payment-methods';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerFindExpensePaymentMethods()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindExpensePaymentMethods$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkExpensePaymentMethod>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerFindExpensePaymentMethodsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkExpensePaymentMethod>>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerFindExpensePaymentMethods$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerFindExpensePaymentMethods(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<WorkExpensePaymentMethod>> {
    return this.accountingV2ControllerFindExpensePaymentMethods$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkExpensePaymentMethod>>): Array<WorkExpensePaymentMethod> => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerCreateExpensePaymentMethod()` */
  static readonly AccountingV2ControllerCreateExpensePaymentMethodPath = '/v2/accounting/expense-payment-methods';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerCreateExpensePaymentMethod()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateExpensePaymentMethod$Response(
    params: {
      body: WorkCreateExpensePaymentMethodDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkExpensePaymentMethod>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerCreateExpensePaymentMethodPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkExpensePaymentMethod>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerCreateExpensePaymentMethod$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerCreateExpensePaymentMethod(
    params: {
      body: WorkCreateExpensePaymentMethodDto
    },
    context?: HttpContext
  ): Observable<WorkExpensePaymentMethod> {
    return this.accountingV2ControllerCreateExpensePaymentMethod$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkExpensePaymentMethod>): WorkExpensePaymentMethod => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerDeleteExpensePaymentMethod()` */
  static readonly AccountingV2ControllerDeleteExpensePaymentMethodPath = '/v2/accounting/expense-payment-methods/{id}';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerDeleteExpensePaymentMethod()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerDeleteExpensePaymentMethod$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<boolean>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerDeleteExpensePaymentMethodPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: String((r as HttpResponse<any>).body) === 'true' }) as StrictHttpResponse<boolean>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerDeleteExpensePaymentMethod$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerDeleteExpensePaymentMethod(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<boolean> {
    return this.accountingV2ControllerDeleteExpensePaymentMethod$Response(params, context).pipe(
      map((r: StrictHttpResponse<boolean>): boolean => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerUpdateExpensePaymentMethod()` */
  static readonly AccountingV2ControllerUpdateExpensePaymentMethodPath = '/v2/accounting/expense-payment-methods/{id}';

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerUpdateExpensePaymentMethod()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateExpensePaymentMethod$Response(
    params: {
      id: number;
      body: WorkUpdateExpensePaymentMethodDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkExpensePaymentMethod>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerUpdateExpensePaymentMethodPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkExpensePaymentMethod>;
      })
    );
  }

  /**
   * Required permissions: accounting:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerUpdateExpensePaymentMethod$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  accountingV2ControllerUpdateExpensePaymentMethod(
    params: {
      id: number;
      body: WorkUpdateExpensePaymentMethodDto
    },
    context?: HttpContext
  ): Observable<WorkExpensePaymentMethod> {
    return this.accountingV2ControllerUpdateExpensePaymentMethod$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkExpensePaymentMethod>): WorkExpensePaymentMethod => r.body)
    );
  }

  /** Path part for operation `accountingV2ControllerGetExchangeRate()` */
  static readonly AccountingV2ControllerGetExchangeRatePath = '/v2/accounting/exchange-rates';

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `accountingV2ControllerGetExchangeRate()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerGetExchangeRate$Response(
    params: {
      currency: string;

    /**
     * YYYY-MM-DD
     */
      date: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkExchangeRateDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkAccountingV2Service.AccountingV2ControllerGetExchangeRatePath, 'get');
    if (params) {
      rb.query('currency', params.currency, {});
      rb.query('date', params.date, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkExchangeRateDto>;
      })
    );
  }

  /**
   * Required permissions: accounting:read, accounting:admin, vouchers:read, vouchers:create, vouchers:update, vouchers:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `accountingV2ControllerGetExchangeRate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  accountingV2ControllerGetExchangeRate(
    params: {
      currency: string;

    /**
     * YYYY-MM-DD
     */
      date: string;
    },
    context?: HttpContext
  ): Observable<WorkExchangeRateDto> {
    return this.accountingV2ControllerGetExchangeRate$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkExchangeRateDto>): WorkExchangeRateDto => r.body)
    );
  }

}
